import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { useRecoilValue } from 'recoil';
import DynamicTable from '../DynamicTable';
import TemplateForm from './Templates/TemplateForm';
import ConfirmDialog from '../ConfirmDialog';
import { TABLE_ONLINE_MAP_ASSIGN, TABLE_TEMPLATES } from '../../Services/TableService';
import { URL_ONLINE_PANEL } from '../../router/constants';
import PermissionsService, {
  PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../Services/PermissionsService';
import TabComponent from '../TabComponent';
import { ONLINE_PANEL_ASSIGN_URL } from './AssignTable';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { ITab } from '../TabComponent/interfaces';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { removeWidgetsFromLocalStorage } from './OnlineTemplateService';

interface IButtons {
  [key: string]: {
    open: string[];
  };
}

const ADD_TEMPLATE_URL = '/online_panel/templates/add';
export const TEMPLATES_URL = '/online_panel/templates';

export const tabsByPermission = (
  tabs: string[],
  active: string,
  selectedCustomers: number[]
): ITab[] => {
  const enabledTabs: ITab[] = [];
  tabs.forEach((tabItem: string) => {
    if (
      tabItem === TABLE_TEMPLATES &&
      (PermissionsService.hasAccess(
        PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
        ['read'],
        selectedCustomers
      ) ||
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
          ['read'],
          selectedCustomers
        ) ||
        PermissionsService.hasAccess(
          PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
          ['read'],
          selectedCustomers
        ))
    ) {
      enabledTabs.push({ name: tabItem, isActive: TABLE_TEMPLATES === active, url: TEMPLATES_URL });
    }
    if (
      tabItem === TABLE_ONLINE_MAP_ASSIGN &&
      PermissionsService.hasAccess(
        PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
        ['read'],
        selectedCustomers
      )
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: TABLE_ONLINE_MAP_ASSIGN === active,
        url: ONLINE_PANEL_ASSIGN_URL,
      });
    }
  });
  return enabledTabs;
};

function OnlinePanel(): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const [reloadTable, setReloadTable] = React.useState<boolean>(false);
  const [editId, setEditId] = React.useState<number | null>(null);
  const [isCancelButtonClicked, setIsCancelButtonClicked] = React.useState<boolean>(false);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_ONLINE_PANEL}/templates/edit/:id`);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const tabs = React.useState(
    tabsByPermission([TABLE_TEMPLATES, TABLE_ONLINE_MAP_ASSIGN], TABLE_TEMPLATES, selectedCustomers)
  )[0];

  const { setDocumentTitle } = useDocumentTitle();

  const handleCloseSubpage = (value?: string) => {
    if (value === 'submit') {
      setIsSubpageOpened(false);
      navigate(TEMPLATES_URL);
    } else {
      setIsCancelButtonClicked(true);
    }
  };

  const handleReloadTable = () => {
    setReloadTable(true);
  };

  const handleSubpage = (id: number | null) => {
    setReloadTable(false);

    if (id) {
      setIsSubpageOpened(true);
      setEditId(id);
      navigate(`${TEMPLATES_URL}/edit/${id}`);
    } else {
      setIsSubpageOpened(true);
      setEditId(null);
      navigate(ADD_TEMPLATE_URL);
    }
  };

  const handleConfirmCancelAction = () => {
    setIsCancelButtonClicked(false);
  };

  const handleConfirmCancelSubpage = () => {
    setIsSubpageOpened(false);
    setIsCancelButtonClicked(false);
    setEditId(null);
    removeWidgetsFromLocalStorage();
    navigate(TEMPLATES_URL);
  };

  const buttons: IButtons = {};

  if (
    PermissionsService.hasAccess(
      PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
      ['create'],
      selectedCustomers
    ) ||
    PermissionsService.hasAccess(
      PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
      ['create'],
      selectedCustomers
    ) ||
    PermissionsService.hasAccess(
      PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
      ['create'],
      selectedCustomers
    )
  ) {
    buttons[TABLE_TEMPLATES] = {
      open: ['addOnlineTemplate'],
    };
  }

  React.useEffect(() => {
    if (pathname === URL_ONLINE_PANEL || pathname === `${URL_ONLINE_PANEL}/templates`) {
      setIsSubpageOpened(false);
      const documentTitle = `${f({ id: 'tab.online-map/templates' })} - ${f({
        id: 'menu.onlinePanel',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === ADD_TEMPLATE_URL) {
      setIsSubpageOpened(true);
      const documentTitle = `${f({ id: 'onlineTemplates.subPage.add.subtitle' })} - ${f({
        id: 'tab.online-map/templates',
      })} - ${f({
        id: 'menu.onlinePanel',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      setEditId(Number(params.id));
      setIsSubpageOpened(true);
      setReloadTable(false);
      const documentTitle = `${f({ id: 'onlineTemplates.subPage.edit.subtitle' })} - ${f({
        id: 'tab.online-map/templates',
      })} - ${f({
        id: 'menu.onlinePanel',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    }

    // return () => {
    //   setIsSubpageOpened(false);
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div>
      <div className="col-12" style={{ display: isSubpageOpened ? 'none' : '' }}>
        <div className="row section page-head">
          <h4>
            {`${f({ id: 'menu.settings' })} / ${f({ id: 'menu.onlinePanel' })}`}
            <OnlineHelp topic="online-panel" />
          </h4>
        </div>
        <TabComponent tabs={tabs}>
          <div>
            <DynamicTable
              name={TABLE_TEMPLATES}
              updateTable={reloadTable}
              openForm={handleSubpage}
              mustClosed={true}
              buttons={buttons[TABLE_TEMPLATES]}
              routerParams={params}
            />
            {isCancelButtonClicked && (
              <ConfirmDialog
                title={f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.title' })}
                cancelCallback={handleConfirmCancelAction}
                continueCallback={handleConfirmCancelSubpage}
                cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
                continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
              >
                {f({ id: 'table.confirm.dialog.onlineTemplates.subpage.close.message' })}
              </ConfirmDialog>
            )}
          </div>
        </TabComponent>
      </div>
      {isSubpageOpened && (
        <TemplateForm
          closeCallback={handleCloseSubpage}
          id={editId}
          reloadTableCallback={handleReloadTable}
        />
      )}
    </div>
  );
}

export default OnlinePanel;
